import React, { useCallback, useEffect, useMemo } from 'react'
import { Spinner } from 'react-bootstrap'

import { selectGroupedTasks, selectTaskPollingEnabled, selectTasksLoading, startTaskPolling, stopTaskPolling } from '../../../../../../../store/task/task'
import { selectShouldPollMeetingTasks } from '../../../../../../../store/meetingSummary/meetingSummary'
import { selectRoomSession } from '../../../../../../../store/meetingSummary/roomSession'
import { useAppDispatch, useAppSelector } from '../../../../../../../store/hooks'

import { AddUserGroupTasks, NewFormTask, UserGroupTasks } from '../../../../../../../views/Tasks/Tasks'

import { Task } from '../../../../../../../models/MeetingSupport'
import { AiFeature } from '../../../../../../../models/AiFeature'
import { AiResult } from '../../../../../../../models/AiResult'

import { Divider } from '../../../../../../../components/Divider/Divider'

import { useSelectedMeetingTemplateHasAgendaEnabled } from '../../../../MeetingPreparation/Agenda/useAgendaFeature'
import { useMeetingSummary } from '../../../MeetingSummaryAgent/hooks'

import { ResultDisplayProps } from '../../common'

import { AgendaBox } from './AgendaBox/AgendaBox'

import './ClientAndAdvisorTasks.scss'
import { useTaskSelectionHandler } from '../../../../common/hooks/useTaskSelectionHandler'

export const ClientAndAdvisorTasks: React.FC<ResultDisplayProps> = ({ aiFeature, aiResult }) => {
  const dispatch = useAppDispatch()

  const shouldPollTasks = useAppSelector(selectShouldPollMeetingTasks)

  useEffect(() => {
    if (shouldPollTasks) {
      dispatch(startTaskPolling())
    } else {
      dispatch(stopTaskPolling())
    }
  }, [dispatch, shouldPollTasks])

  return (
    <>
      <ClientAndAdvisorTasksContent aiFeature={aiFeature} aiResult={aiResult} />
    </>
  )
}

interface ClientAndAdvisorTasksContentProps {
  aiFeature: AiFeature
  aiResult: AiResult
}

export const ClientAndAdvisorTasksContent: React.FC<ClientAndAdvisorTasksContentProps> = ({ aiFeature, aiResult }) => {
  const isLoading = useAppSelector(selectTasksLoading)
  const groupedTasks = useAppSelector(selectGroupedTasks)
  const roomSession = useAppSelector(selectRoomSession)
  const isPolling = useAppSelector(selectTaskPollingEnabled)

  const isAgendaEnabledForSelectedMeetingTemplate = useSelectedMeetingTemplateHasAgendaEnabled()

  const { handleMeetingSummaryResultEdited } = useMeetingSummary()
  const { selectionHandlers } = useTaskSelectionHandler()

  const contactsForMeeting = useMemo(() => roomSession?.users.filter((e) => e.type === 'external') ?? [], [roomSession])

  const taskActionCallback = useCallback(
    (editedAiResult: Task[]) => handleMeetingSummaryResultEdited(aiFeature, aiResult.id, editedAiResult),
    [aiFeature, aiResult.id, handleMeetingSummaryResultEdited],
  )

  const formTaskToCreateTaskPayload = useCallback(
    (assignedToUserId: string, formTask: NewFormTask) => {
      const { subject, description, relatedToUserId } = formTask
      const { roomId, sessionId } = aiResult
      return {
        ...(relatedToUserId ? { relatedTo: { type: 'user', id: relatedToUserId } } : {}),
        ...(description ? { description } : {}),
        assignedTo: { type: 'user', id: assignedToUserId },
        task: subject,
        type: 'meeting',
        roomId,
        sessionId,
      }
    },
    [aiResult],
  )

  if (isLoading || isPolling) {
    return (
      <div className='d-flex justify-content-center'>
        <Spinner />
      </div>
    )
  }

  const defaultRelatedToOptions = roomSession?.users ?? []
  const initialRelatedToUser = contactsForMeeting.length === 1 ? contactsForMeeting[0] : null

  return (
    <div className='pt-3 px-2 d-flex flex-column gap-4'>
      {isAgendaEnabledForSelectedMeetingTemplate ? <AgendaBox /> : null}
      {groupedTasks.map(({ user, tasks }, index) => (
        <React.Fragment key={user.id}>
          <UserGroupTasks
            user={user}
            tasks={tasks}
            row={index}
            trackingSource='pastMeeting'
            formTaskToCreateTaskPayload={formTaskToCreateTaskPayload}
            taskActionCallback={taskActionCallback}
            defaultRelatedToOptions={defaultRelatedToOptions}
            initialRelatedToUser={initialRelatedToUser}
            selectionHandlers={selectionHandlers}
            showAssignAll={true}
            roomId={aiResult.roomId}
          />
          {index !== groupedTasks.length - 1 ? <Divider /> : null}
        </React.Fragment>
      ))}
      <AddUserGroupTasks
        formTaskToCreateTaskPayload={formTaskToCreateTaskPayload}
        taskActionCallback={taskActionCallback}
        defaultRelatedToOptions={defaultRelatedToOptions}
        initialRelatedToUser={initialRelatedToUser}
        roomId={aiResult.roomId}
      />
    </div>
  )
}
